import { BackLink } from '@/components/BackLink';
import { Button } from '@/components/Button';
import { Callout } from '@/components/Callout';
import { DeadEndHelpButton } from '@/components/DeadEndHelpButton';
import { KioskCard } from '@/components/KioskCard';
import { KioskScreen } from '@/components/KioskScreen';
import { StatusBadge } from '@/components/StatusBadge';
import { ARRIVAL_REASON } from '@/features/arrival/types';
import type { TranslationKey } from '@/i18n/dictionary';
import { useTranslation } from '@/i18n/useTranslation';
import { useKioskSession } from '@/lib/kioskSession';
import { useBack } from '@/shared/hooks/useBack';
import { useFocusOnMount } from '@/shared/hooks/useFocusOnMount';

/**
 * The hand-off to a person [Figma · 04 · See a rep, `12021:40515` · `40500` · `40486`].
 *
 * **It used to be a dead end that said nothing.** Every route into it — an ambiguous match, a barred record,
 * a lookup that timed out, and every arrival reason whose own screen does not exist yet — rendered the same
 * four words, *"Please see a representative"*, with one control that wiped the session. Somebody who had
 * simply tapped "Complete my drug test" was told to go to the desk, not told why, and could not go back.
 *
 * The frame answers all three: a Back control, an amber **Rep** chip in place of the kiosk one, and a
 * callout that names what the candidate came for. Built on the Operator's report of 2026-09-02.
 *
 * **The reason shown is the candidate's own declaration**, taken from the arrival menu — never a diagnosis.
 * That distinction is decision 0001's: a lobby screen may repeat what the person just chose, and may not
 * disclose what the lookup concluded about them. When there is no declaration (the lookup failed before the
 * menu) the callout is simply absent, which is why it is conditional rather than defaulted to a guess.
 *
 * ⭐ **Reaching it raises the candidate's queue entry, with no tap** [0500; 03-us-kid-7 scenario *Reaching the
 * neutral hand-off raises the entry*]. Its copy says a representative has been told, so an entry stands behind
 * the words [0220 L1]: `stuck at sign-in`, `asked for help`, raised with no name and no candidate id (the entry
 * still carries a name the session already holds [0500 § *What the auto-raised entry carries*; KOP-1d AC-5]),
 * once per candidate session (`KioskScreen`'s `raisesOnArrival`). *Get help* then sends nothing new and opens the *Need help?*
 * overlay on `in the recruiter queue` (O4); *Start over* ends the session and leaves the entry in place
 * [0500 point 5; 0202].
 */
export function SeeARepScreen() {
  const t = useTranslation();
  const heading = useFocusOnMount<HTMLHeadingElement>();
  const back = useBack();
  const arrival = useKioskSession((state) => state.arrival);
  const endCandidateSession = useKioskSession((state) => state.endCandidateSession);

  const reasonKey: TranslationKey | undefined =
    arrival === undefined ? undefined : (`arrival.${arrival.reason}.title` as TranslationKey);

  return (
    // The neutral hand-off is a sign-in dead end: *Need help?* here reads *stuck at sign-in* [0230; 0461], and
    // arriving here raises that same entry [0500].
    <KioskScreen raisesOnArrival signInDeadEnd>
      <KioskCard
        footer={
          <div className="flex w-full flex-col gap-4 sm:flex-row sm:items-center sm:justify-end">
            {/*
              The screen's own help control [KID-7; 0230 § Decision 1]. The frame draws none: a departure. The
              entry is already raised on arrival, so here it only opens the queue state [0500 point 5].
            */}
            <DeadEndHelpButton />
            <Button onClick={endCandidateSession} variant="secondary">
              {t('routing.assistance.startOver')}
            </Button>
          </div>
        }
        header={
          <>
            <BackLink onClick={back} />
            <StatusBadge label={t('seeARep.badge')} tone="rep" />
          </>
        }
      >
        {/* 12px between blocks, measured: the icon ends at 355 and the heading opens at 367. */}
        <div className="flex w-full flex-col items-center gap-3 pb-8 text-center">
          <span
            aria-hidden="true"
            className="flex size-14 items-center justify-center rounded-[var(--radius-full)] bg-[var(--primary-muted)]"
          >
            <svg fill="none" height="24" viewBox="0 0 24 24" width="24">
              <path
                d="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm0 2c-4 0-7 2-7 5v1h14v-1c0-3-3-5-7-5Z"
                stroke="var(--primary)"
                strokeLinejoin="round"
                strokeWidth="1.5"
              />
            </svg>
          </span>
          <h1
            className="kiosk-heading-focus w-full text-2xl font-bold leading-7 tracking-[-0.36px] text-black"
            ref={heading}
            tabIndex={-1}
          >
            {t('seeARep.heading')}
          </h1>
          <p className="w-full text-sm leading-6 text-[var(--muted-foreground)]">
            {t('seeARep.body')}
          </p>
          {reasonKey !== undefined && (
            <Callout>{t('seeARep.reason', { reason: t(reasonKey) })}</Callout>
          )}
          {/*
            Only for the I-9. The frame draws this amber panel on the I-9 hand-off and nowhere else, and it
            is the one reason a candidate has to bring something with them — telling everyone to fetch
            documents would send people home for papers they do not need.
          */}
          {arrival?.reason === ARRIVAL_REASON.onboardingPaperwork && (
            <Callout tone="warning">{t('seeARep.documents')}</Callout>
          )}
        </div>
      </KioskCard>
    </KioskScreen>
  );
}
